import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { contactFormSchema, subjectOptions, type ContactFormData } from "@/lib/validation";
import { sanitizeFormData } from "@/lib/sanitize";

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      subject: undefined,
      message: "",
      newsletter: false,
    },
    mode: "onBlur",
  });

  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      // Sanitize data before sending
      const sanitizedData = sanitizeFormData(data);

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);

        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          form.reset();
        }, 5000);
      } else {
        // Handle server-side validation errors
        if (result.fieldErrors) {
          Object.entries(result.fieldErrors).forEach(([field, message]) => {
            form.setError(field as keyof ContactFormData, {
              type: "server",
              message: message as string,
            });
          });
        } else {
          console.error('Form submission failed:', result.error);
          alert(result.error || 'Failed to send message. Please try again or contact us directly at 01923 620240/41.');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('An unexpected error occurred. Please try again or contact us directly at 01923 620240/41.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input class helper for error styling
  const getInputClassName = (hasError: boolean) => {
    const baseClass = "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors";
    return hasError
      ? `${baseClass} border-red-500 focus:ring-red-500`
      : `${baseClass} border-gray-300 focus:ring-wl-blue`;
  };

  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="Contact Us - Get In Touch Today"
        description="Contact Whitefield Legal Services for property search enquiries. Call 01923 620240/41 or send us a message. Office in Northwood Hills, HA6 1NW."
        keywords="contact whitefield legal, property search enquiry, conveyancing search quote"
        canonicalUrl="/contact"
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Contact" }])}
      />
      <Navbar />

      {/* Hero Section with Video Backdrop */}
      <section className="relative py-32 bg-wl-navy overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/contact-back.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>

        {/* Single overlay for darkening */}
        <div className="absolute inset-0 bg-wl-navy/80"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Ready to discuss your property search requirements? Get in touch with our experienced team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

            {/* Contact Form */}
            <div className="bg-gray-50 p-8 rounded-2xl scroll-animate">
              <h2 className="text-3xl font-bold text-wl-navy mb-6">Send Us a Message</h2>

              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-wl-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-wl-green" />
                  </div>
                  <h3 className="text-xl font-bold text-wl-navy mb-2">Message Sent Successfully!</h3>
                  <p className="text-wl-gray mb-4">
                    Thank you for your enquiry. We have received your message and will respond within 24 hours.
                  </p>
                  <p className="text-sm text-wl-gray">
                    A confirmation email has been sent to your email address.
                  </p>
                  <div className="mt-4 p-3 bg-wl-light rounded-lg">
                    <p className="text-sm text-wl-navy">
                      <strong>For urgent matters:</strong> Call us directly at{' '}
                      <a href="tel:01923620240" className="text-wl-blue hover:underline">
                        01923 620240/41
                      </a>
                    </p>
                  </div>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-wl-navy mb-2">
                              Full Name *
                            </FormLabel>
                            <FormControl>
                              <input
                                {...field}
                                type="text"
                                className={getInputClassName(!!fieldState.error)}
                                placeholder="Your full name"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 text-sm mt-1" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-wl-navy mb-2">
                              Email Address *
                            </FormLabel>
                            <FormControl>
                              <input
                                {...field}
                                type="email"
                                className={getInputClassName(!!fieldState.error)}
                                placeholder="your.email@example.com"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 text-sm mt-1" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-wl-navy mb-2">
                              Company/Firm
                            </FormLabel>
                            <FormControl>
                              <input
                                {...field}
                                type="text"
                                className={getInputClassName(!!fieldState.error)}
                                placeholder="Your company name"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 text-sm mt-1" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-wl-navy mb-2">
                              Phone Number
                            </FormLabel>
                            <FormControl>
                              <input
                                {...field}
                                type="tel"
                                className={getInputClassName(!!fieldState.error)}
                                placeholder="Your phone number"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 text-sm mt-1" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-wl-navy mb-2">
                            Subject *
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`w-full px-4 py-3 h-auto border rounded-lg focus:ring-2 focus:border-transparent bg-white ${
                                  fieldState.error
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-wl-blue"
                                }`}
                              >
                                <SelectValue placeholder="Please select a subject" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white">
                              {subjectOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 text-sm mt-1" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="newsletter"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-start space-x-3">
                            <FormControl>
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="mt-1 h-4 w-4 text-wl-blue focus:ring-wl-blue focus:ring-2 border-gray-300 rounded"
                              />
                            </FormControl>
                            <FormLabel className="text-sm text-wl-gray cursor-pointer">
                              Would you like to stay in touch with us via our newsletter? You may unsubscribe at any time.
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-wl-navy mb-2">
                            Message *
                          </FormLabel>
                          <FormControl>
                            <textarea
                              {...field}
                              rows={6}
                              className={`${getInputClassName(!!fieldState.error)} resize-none`}
                              placeholder="Please provide details about your requirements..."
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 text-sm mt-1" />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-wl-green hover:bg-wl-green/90 text-white py-3 text-lg font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-8 scroll-animate">
              <div>
                <h2 className="text-3xl font-bold text-wl-navy mb-6">Get In Touch</h2>
                <p className="text-xl text-wl-gray mb-8">
                  We're here to help with all your property search needs. Contact us using any of the methods below.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-wl-blue scroll-animate">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-wl-blue/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <Phone className="w-6 h-6 text-wl-blue" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-wl-navy mb-2">Phone</h3>
                      <p className="text-wl-gray mb-2">Call us for immediate assistance</p>
                      <a href="tel:01923620240" className="text-wl-blue font-semibold hover:underline">
                        01923 620240/41
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-wl-green scroll-animate">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-wl-green/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <Mail className="w-6 h-6 text-wl-green" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-wl-navy mb-2">Email</h3>
                      <p className="text-wl-gray mb-2">Send us your requirements</p>
                      <a href="mailto:admin@whitefieldlegal.com" className="text-wl-green font-semibold hover:underline">
                        admin@whitefieldlegal.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-wl-navy scroll-animate">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-wl-navy/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <MapPin className="w-6 h-6 text-wl-navy" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-wl-navy mb-2">Address</h3>
                      <p className="text-wl-gray mb-2">Visit our office</p>
                      <div className="text-wl-navy">
                        <div>2nd Floor Argyle House</div>
                        <div>Joel Street, Northwood Hills</div>
                        <div>HA6 1NW</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-wl-blue scroll-animate">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-wl-blue/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <Clock className="w-6 h-6 text-wl-blue" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-wl-navy mb-2">Business Hours</h3>
                      <div className="text-wl-gray space-y-1">
                        <div>Monday - Friday: 9:00 AM - 5:30 PM</div>
                        <div>Saturday: 9:00 AM - 1:00 PM</div>
                        <div>Sunday: Closed</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-wl-light p-6 rounded-xl border border-wl-green/20">
                <h3 className="text-lg font-bold text-wl-navy mb-2">Urgent Searches?</h3>
                <p className="text-wl-gray text-sm mb-3">
                  Need same-day service for urgent completions? Call us directly for priority processing.
                </p>
                <a
                  href="tel:01923620240"
                  className="inline-flex items-center bg-wl-green hover:bg-wl-green/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now for Urgent Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
